import React, { useEffect, useRef } from 'react';
import { Box, Button, Link, Stack, SubtleText, useToggle } from '@99designs/ui';
import { FormattedReactMessage } from '@99designs/frontend-components/i18n';
import { getSupportContactUrl } from '@99designs/frontend-components/utils/platform/supportUrlHelpers';
import { FloatingAlert, StatusMessage } from '@99designs/purgatory';
import { ErrorType } from './types';
import { useTwoFactorUI } from './hooks';

const errorMessages: { [type in ErrorType]: string } = {
    TOKEN_ATTEMPTS: __('Too many failed attempts. Please <Link>request a new code</Link>'),
    TOKEN_EMPTY: __('Please enter your security code'),
    TOKEN_EXPIRED: __('This code is no longer valid. Please <Link>request a new code</Link>'),
    TOKEN_INVALID: __('Your code is invalid. Please try again'),
};

export function VerifyAddPayoutForm() {
    const { resendRequestCount, resendToken, verifyToken, uiState, error } = useTwoFactorUI();

    const inputEl = useRef<HTMLInputElement>(null);
    const [showSuccess, { on: toggleOnShowSuccess, off: toggleOffShowSuccess }] = useToggle(false);
    const [
        showDefaultError,
        { on: toggleOnShowDefaultError, off: toggleOffShowDefaultError },
    ] = useToggle(false);

    useEffect(() => {
        if (uiState === 'ERROR' && error === null) {
            toggleOnShowDefaultError();
        } else if (uiState === 'SUCCESS' || uiState === 'RESEND_SUCCESS') {
            toggleOnShowSuccess();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [error, uiState]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const t = inputEl?.current ? inputEl.current.value : '';
        verifyToken(t);
    };

    return (
        <>
            <form onSubmit={handleSubmit}>
                <div
                    className="field"
                    data-field-error={uiState === 'ERROR' && error ? true : undefined}
                >
                    <label className="field__title" htmlFor="security-code">
                        {__('Enter code')}
                    </label>
                    {uiState === 'ERROR' && error && (
                        <StatusMessage type="error">
                            <span data-testid="error-message">
                                <FormattedReactMessage
                                    message={errorMessages[error]}
                                    replacements={{
                                        Link: (
                                            <Link
                                                data-testid="resend-token-error"
                                                color="risingSun"
                                                onClick={resendToken}
                                            />
                                        ),
                                    }}
                                />
                            </span>
                        </StatusMessage>
                    )}
                    <div className="field__input">
                        <input
                            ref={inputEl}
                            aria-label={__('Enter security code')}
                            placeholder={__('Security code')}
                            id="security-code"
                            name="token"
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]{6}"
                            autoComplete="one-time-code"
                            className="input-text input-width--short"
                        />
                    </div>
                </div>
                <Box marginTop="space12" marginBottom="halfGutter">
                    <Button
                        data-testid="verify-token"
                        appearance="primary"
                        loading={uiState === 'LOADING'}
                        disabled={uiState === 'LOADING' || uiState === 'RESEND_LOADING'}
                        type="submit"
                    >
                        {__('Submit')}
                    </Button>
                </Box>
            </form>
            <Box marginTop="gutter" marginBottom="halfGutter">
                <Stack space="space6">
                    <SubtleText>
                        <FormattedReactMessage
                            message={
                                resendRequestCount === 0
                                    ? __("Didn't receive a code? <Link>Resend code</Link>")
                                    : __("Still didn't receive a code? <Link>Resend code</Link>")
                            }
                            replacements={{
                                Link: (
                                    <Link
                                        data-testid="resend-token"
                                        color="fiftyFirstShade"
                                        onClick={resendToken}
                                        size="size2"
                                    />
                                ),
                            }}
                        />
                    </SubtleText>
                    {resendRequestCount > 1 && (
                        <SubtleText>
                            <FormattedReactMessage
                                message={__(
                                    "Make sure to check your spam folder. If you still haven't received a code please <Link>contact support</Link>"
                                )}
                                replacements={{
                                    Link: (
                                        <Link
                                            color="fiftyFirstShade"
                                            href={getSupportContactUrl(__LANG__)}
                                            size="size2"
                                        />
                                    ),
                                }}
                            />
                        </SubtleText>
                    )}
                </Stack>
            </Box>
            <FloatingAlert
                data-testid="error-message-toast"
                isOpen={showDefaultError}
                message={
                    <FormattedReactMessage
                        message={__('Something went wrong, please <Link>contact support</Link>')}
                        replacements={{
                            Link: <Link color="bleachedAs" href={getSupportContactUrl(__LANG__)} />,
                        }}
                    />
                }
                alertType="error"
                onClose={toggleOffShowDefaultError}
            />
            <FloatingAlert
                data-testid="success-message"
                isOpen={showSuccess}
                message={
                    uiState === 'RESEND_SUCCESS'
                        ? __('Code re-sent')
                        : __('Code accepted. Redirecting... ')
                }
                alertType="success"
                onClose={toggleOffShowSuccess}
            />
        </>
    );
}
